import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  DatePicker,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import DepositModel from './DepositModel';
import WithdrawalModel from './WithdrawalModel';
import dayjs, { Dayjs } from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
const { RangePicker } = DatePicker;

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Wallets {
  wallet_id: number;
  wallet_ledger_id?: string;
  wallet_type?: string;
  wallet_category?: string;
  wallet_remark?: string;
  wallet_reserve_fund?: string;
  wallet_tds?: string;
  wallet_other?: string;
  wallet_amount?: string;
  wallet_status: number;
  ledger_first_name?: string;
  ledger_last_name?: string;
  wallet_created_on: string;
}

const Wallets: React.FC = () => {
  const [wallets, setWallets] = useState<Wallets[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Wallets | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const [isWithdrawalModalVisible, setIsWithdrawalModalVisible] = useState(false);
  const [isEditWithdrawalMode, setIsEditWithdrawalMode] = useState(false);
  const [initialWithdrawalValues, setInitialWithdrawalValues] = useState<Wallets | null>(null);

  const [filterLedgerName, setFilterLedgerName] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | undefined>();
  const [filterLedgerId, setFilterLedgerId] = useState<string | undefined>();

  const [filterDateRange, setFilterDateRange] = useState<any>(null);

  const [filteredData, setFilteredData] = useState<Wallets[]>([]);

  const [filterStartDate, setFilterStartDate] = useState<string | undefined>();
  const [filterEndDate, setFilterEndDate] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();


  const [ledgerList, setLedgerList] = useState<{ ledger_id: string, ledger_first_name: string, ledger_last_name: string }[]>([]);


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // const fetchWallets = async () => {
  //   try {
  //     const data = await apiRequest<Wallets[]>({
  //       method: 'get',
  //       url: 'wallet',
  //     });
  //     setWallets(data);
  //   } catch (error) {
  //     showError('Failed to fetch wallets');
  //     console.error(error);
  //   }
  // };

  const fetchWallets = async () => {
    try {
      const params: Record<string, string | undefined> = {
        ledger_id: filterLedgerId,
        category: filterCategory,
        start_date: filterStartDate,
        end_date: filterEndDate,
        status: filterStatus,
      };

      // Remove undefined params
      Object.keys(params).forEach(
        key => params[key] === undefined && delete params[key]
      );

      const data = await apiRequest<Wallets[]>({
        method: 'get',
        url: 'wallet', // 👈 ensure it matches backend route
        params,
      });

      setWallets(data);
    } catch (error) {
      showError('Failed to fetch wallets');
      console.error(error);
    }
  };

  async function fetchWalletLedgers() {
    try {
      const response = await apiRequest({
        method: 'get',
        url: '/h-ledger-data?category=tailor'   // your API
      });

      console.log("Wallet Ledger Data:", response);

      setLedgerList(response.ledgers);

    } catch (err) {
      showError("Failed to load wallet ledger names");
    }
  }
  useEffect(() => {
    fetchWallets();
  }, [filterLedgerId, filterCategory, filterStartDate, filterEndDate, filterStatus]);

  useEffect(() => {
    fetchWalletLedgers();
  }, []);




  const handleEditClick = (record: Wallets) => {
    setIsEditMode(true);
    setInitialValues({
      wallet_id: record.wallet_id,
      wallet_type: record.wallet_type,
      wallet_ledger_id: record.wallet_ledger_id,
      wallet_category: record.wallet_category,
      wallet_remark: record.wallet_remark,
      wallet_reserve_fund: record.wallet_reserve_fund,
      wallet_tds: record.wallet_tds,
      wallet_other: record.wallet_other,
      wallet_amount: record.wallet_amount,
      wallet_status: record.wallet_status,
      wallet_created_on: record.wallet_created_on,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchWallets();
  };


  const handleWithdrawalCreate = () => {
    fetchWallets();
  };

  const handleWithdrawalEditClick = (record: Wallets) => {
    setIsEditWithdrawalMode(true);
    setInitialWithdrawalValues({
      wallet_id: record.wallet_id,
      wallet_type: record.wallet_type,
      wallet_ledger_id: record.wallet_ledger_id,
      wallet_category: record.wallet_category,
      wallet_remark: record.wallet_remark,
      wallet_reserve_fund: record.wallet_reserve_fund,
      wallet_tds: record.wallet_tds,
      wallet_other: record.wallet_other,
      wallet_amount: record.wallet_amount,
      wallet_status: record.wallet_status,
      wallet_created_on: record.wallet_created_on,
    });
    setIsWithdrawalModalVisible(true);
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const showCreateWithdrawalModal = () => {
    setIsEditWithdrawalMode(false);
    setInitialWithdrawalValues(null);
    setIsWithdrawalModalVisible(true);
  };

  const hideCreateWithdrawalModal = () => {
    setIsWithdrawalModalVisible(false);
    setIsEditWithdrawalMode(false);
    setInitialWithdrawalValues(null);
  };

  const handleDelete = async (wallet_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-wallet/${wallet_id}`,
      });
      showSuccess('wallet deleted');
      fetchWallets();
    } catch {
      showError('Failed to delete wallet');
    }
  };

  const handleStatusToggle = async (WalletId: number) => {
    setWallets((prev) =>
      prev.map((item) =>
        item.wallet_id === WalletId
          ? { ...item, wallet_status: item.wallet_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-wallet/${WalletId}`,
      });
      showSuccess('wallet status updated');
    } catch (error) {
      showError('Failed to update status');
      setWallets((prev) =>
        prev.map((item) =>
          item.wallet_id === WalletId
            ? { ...item, wallet_status: item.wallet_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Wallets>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };


  const applyFilters = () => {

    let filtered = [...wallets];

    // Ledger name filter
    if (filterLedgerName) {
      filtered = filtered.filter(w => {
        const full = `${w.ledger_first_name ?? ""} ${w.ledger_last_name ?? ""}`.trim();
        return full === filterLedgerName;
      });
    }

    // Category filter
    if (filterCategory) {
      filtered = filtered.filter(w => w.wallet_category === filterCategory);
    }

    // Date filter (assuming wallet_date)
    if (filterDateRange && filterDateRange.length === 2) {
      const [start, end] = filterDateRange;
      filtered = filtered.filter(w => {
        const d = new Date(w.wallet_created_on);   // <-- adjust field name!
        return d >= new Date(start) && d <= new Date(end);
      });
    }

    setFilteredData(filtered);   // ← NEW DATA SOURCE
  };



  useEffect(() => {
    setFilteredData(wallets);
  }, [wallets]);



  const columns: TableColumnsType<Wallets> = [
    {
      title: 'Name',
      width: 200,
      dataIndex: 'ledger_first_name',
      key: 'ledger_first_name',
      filters: Array.from(
        new Set(
          wallets.map((s: any) =>
            `${s.ledger_first_name ?? ''} ${s.ledger_last_name ?? ''}`.trim()
          )
        )
      )
        .filter((name) => name !== '') // remove blank values
        .map((name) => ({
          text: name as string,
          value: name as string,
        })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.ledger_first_name || null,
      onFilter: (value, record) => {
        const fullName = `${record.ledger_first_name ?? ''} ${record.ledger_last_name ?? ''}`;
        return fullName.includes(value as string);
      },
      sorter: (a, b) => {
        const nameA = `${a.ledger_first_name ?? ''} ${a.ledger_last_name ?? ''}`;
        const nameB = `${b.ledger_first_name ?? ''} ${b.ledger_last_name ?? ''}`;
        return nameA.localeCompare(nameB);
      },

      sortOrder: sortedInfo.columnKey === 'ledger_first_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      // ---------- Display ----------
      render: (_, record) => (
        <div>{`${record.ledger_first_name ?? ''} ${record.ledger_last_name ?? ''}`}</div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'wallet_type',
      key: 'wallet_type',
      width: 100,
      filters: Array.from(
        new Set(
          wallets.map((s) => s.wallet_type || "") // remove undefined
        )
      )
        .filter(Boolean) // remove empty strings
        .map((name) => ({
          text: name,
          value: name,
        })),

      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.wallet_type || null,

      onFilter: (value, record) =>
        (record.wallet_type || "").includes(value as string),

      sorter: (a, b) =>
        (a.wallet_type || "").localeCompare(b.wallet_type || ""),

      sortOrder: sortedInfo.columnKey === 'wallet_type' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.wallet_type}</div>,
    },
    {
      title: 'Category',
      dataIndex: 'wallet_category',
      key: 'wallet_category',
      width: 100,
      filters: Array.from(
        new Set(wallets.map((s) => s.wallet_category || ""))
      )
        .filter(Boolean)
        .map((name) => ({
          text: name,
          value: name,
        })),

      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.wallet_category || null,

      onFilter: (value, record) =>
        (record.wallet_category || "").includes(value as string),

      sorter: (a, b) =>
        (a.wallet_category || "").localeCompare(b.wallet_category || ""),

      sortOrder:
        sortedInfo.columnKey === 'wallet_category' ? sortedInfo.order : null,

      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.wallet_category}</div>,
    },
    {
      title: 'Amount',
      dataIndex: 'wallet_amount',
      key: 'wallet_amount',
      width: 150,
      // FIX 1: Avoid undefined in filters
      filters: Array.from(
        new Set(wallets.map((s) => s.wallet_amount || ""))
      ).map((amount) => ({
        text: amount || "",
        value: amount || "",
      })),

      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.wallet_amount || null,

      // FIX 2: Always use string fallback
      onFilter: (value, record) =>
        (record.wallet_amount || "").includes(value as string),

      // FIX 3: Safe string comparison
      sorter: (a, b) =>
        (a.wallet_amount || "").localeCompare(b.wallet_amount || ""),

      sortOrder:
        sortedInfo.columnKey === "wallet_amount" ? sortedInfo.order : null,

      sortDirections: ["ascend", "descend"],
      ellipsis: true,

      render: (_, record) => <div>{record.wallet_amount}</div>,
    },
    {
      title: "Remark",
      dataIndex: "wallet_remark",
      key: "wallet_remark",
      width: 200,

      // FIX: Remove undefined + convert to string
      filters: Array.from(
        new Set(wallets.map((s) => s.wallet_remark || ""))
      )
        .filter(Boolean) // remove ""
        .map((name) => ({
          text: name,
          value: name,
        })),

      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.wallet_remark || null,

      // FIX: ensure string
      onFilter: (value, record) =>
        (record.wallet_remark || "").includes(value as string),

      // FIX: safe sorting
      sorter: (a, b) =>
        (a.wallet_remark || "").localeCompare(b.wallet_remark || ""),

      sortOrder:
        sortedInfo.columnKey === "wallet_remark" ? sortedInfo.order : null,

      sortDirections: ["ascend", "descend"],
      ellipsis: true,

      render: (_, record) => <div>{record.wallet_remark}</div>,
    },
    {
      title: "Created On",
      dataIndex: "wallet_created_on",
      key: "wallet_created_on",
      width: 180,
      render: (value: string) => {
        if (!value) return "-";
        const date = dayjs(value);
        return date.isValid() ? date.format("DD-MM-YYYY hh:mm A") : "-";
      },
      sorter: (a, b) =>
        dayjs(a.wallet_created_on).unix() - dayjs(b.wallet_created_on).unix(),
      sortDirections: ["ascend", "descend"],
    }

  ];


  // Conditionally add action column
  // if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
  //   columns.push({
  //     title: 'Action',
  //     width: 200,
  //     render: (_: any, record: Wallets) => (
  //       <Space size="middle">
  //         {currentModule?.right_edit === 'Y' && (
  //           <>
  //             <Switch
  //               checkedChildren="Active"
  //               unCheckedChildren="Inactive"
  //               checked={!!record.wallet_status}
  //               onChange={() => handleStatusToggle(record.wallet_id)}
  //             />
  //             <Button onClick={() => handleWithdrawalEditClick(record)} className="px-8">
  //               <EditOutlined />
  //             </Button>
  //           </>
  //         )}
  //         {currentModule?.right_delete === 'Y' && (
  //           <Popconfirm
  //             title="Delete this wallet?"
  //             onConfirm={() => handleDelete(record.wallet_id)}
  //             onCancel={cancel}
  //             okText="Yes"
  //             cancelText="No"
  //           >
  //             <Button danger className="px-8">
  //               <DeleteOutlined />
  //             </Button>
  //           </Popconfirm>
  //         )}
  //       </Space>
  //     ),
  //   });
  // }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View wallet</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small" style={{ paddingTop: 20 }}>
            <Form.Item >
              <Select
                allowClear
                placeholder="Select Ledger"
                value={filterLedgerId}
                onChange={(value) => setFilterLedgerId(value)}
                style={{ width: 200 }}
                showSearch
              >
                {ledgerList.map((ledger) => (
                  <Select.Option key={ledger.ledger_id} value={ledger.ledger_id}>
                    {ledger.ledger_first_name} {ledger.ledger_last_name}

                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Category Filter */}
            <Form.Item >
              <Select
                allowClear
                placeholder="Select Category"
                value={filterCategory}
                onChange={setFilterCategory}
                style={{ width: 150 }}
              >
                <Select.Option value="debit">Debit</Select.Option>
                <Select.Option value="credit">Credit</Select.Option>
              </Select>
            </Form.Item>

            {/* Date Range Filter */}
            <Form.Item >
              <RangePicker
                value={
                  filterStartDate && filterEndDate
                    ? [dayjs(filterStartDate), dayjs(filterEndDate)]
                    : undefined
                }
                onChange={(dates, dateStrings) => {
                  // `dates` can be null, so we check first
                  if (!dates) {
                    setFilterStartDate(undefined);
                    setFilterEndDate(undefined);
                    return;
                  }
                  setFilterStartDate(dateStrings[0] || undefined);
                  setFilterEndDate(dateStrings[1] || undefined);
                }}
              />
            </Form.Item>
            <Button type="primary" onClick={showCreateWithdrawalModal}>
              Withdrawal
            </Button>
            <Button type="primary" onClick={showCreateModal}>
              Deposit
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Wallets>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="wallet_id"
          dataSource={filteredData.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}

          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No wallet found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, wallets.length)} of{' '}
                {wallets.length}
              </span>
              <Pagination
                {...pagination}
                total={wallets.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <DepositModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        LedgerData={ledgerList}
      />

      <WithdrawalModel
        visible={isWithdrawalModalVisible}
        onCancel={hideCreateWithdrawalModal}
        onCreate={handleWithdrawalCreate}
        isEditWithdrawalMode={isEditWithdrawalMode}
        initialWithdrawalValues={initialWithdrawalValues}
        LedgerData={ledgerList}
      />

    </>
  );
};

export default Wallets;